import { OFFLINE_STORES, getOfflineStorageDriver } from './offlineStorageAdapter.js';
import { isTodayDate } from './visitas/visitasUiService.js';

const STORE = OFFLINE_STORES.LISTS;
const MAX_PAGES = 100;
const MAX_BYTES = 8 * 1024 * 1024;
const MAX_AGE = 7 * 86400000;
const tasks = new Map();
const stable = value => JSON.stringify(value, (_, item) => item && typeof item === 'object' && !Array.isArray(item)
    ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item);
const hash = value => { let n = 2166136261; for (const c of value) n = Math.imul(n ^ c.charCodeAt(0), 16777619); return (n >>> 0).toString(16); };
const failure = (code, message, status) => Object.assign(new Error(message), { code, ...(status ? { status } : {}) });
export const VISITAS_CATALOG_CHANGED = 'visitas:catalog-changed';
export const VISITAS_BRANCHES_OBSERVED = 'visitas:branches-observed';
export function visitasListIdentity(user) {
    const scope = visitasListScope(user);
    return scope ? { scope, prefix: `list:${Number(user.id)}:${hash(scope)}` } : null;
}

export function visitasListScope(user) {
    const permissions = new Set(user?.permissions || []);
    if (!Number.isSafeInteger(Number(user?.id)) || Number(user.id) <= 0 || !Number.isSafeInteger(Number(user?.empresa_id)) || Number(user.empresa_id) <= 0
        || !['empresa', 'cliente', 'sucursal'].includes(user?.tipo_acceso)
        || !permissions.has('access.visitas') || !permissions.has('visitas.view_any')
        || !['visitas.view_empresa', 'visitas.view_all', 'visitas.view_asignadas', 'visitas.view_cliente_sucursales'].some(p => permissions.has(p))) return null;
    return stable({ userId: Number(user.id), empresaId: Number(user.empresa_id), type: user.tipo_acceso,
        permissions: [...permissions].sort(), roles: (user.roles || []).map(r => ({ id: r.id, tipo_acceso: r.tipo_acceso, hierarchy_level: r.hierarchy_level })).sort((a,b) => Number(a.id)-Number(b.id)) });
}

function queryOf(params) {
    if (!Number.isSafeInteger(Number(params.page || 1)) || Number(params.page || 1) < 1) throw failure('invalid_query', 'La página solicitada no es válida.', 422);
    const query = { tab: params.tab || 'pendientes', per_page: Number(params.per_page || 20), sort_by: params.sort_by || 'fecha', sort_dir: params.sort_dir || 'desc' };
    if (!['pendientes','iniciadas','iniciadas_no_finalizadas','finalizadas','aprobadas','vencidas'].includes(query.tab)
        || ![10,20,50].includes(query.per_page) || !['fecha','numero','estado','cliente','sucursal','servicio'].includes(query.sort_by)
        || !['asc','desc'].includes(query.sort_dir)) throw failure('invalid_query', 'Los filtros del listado no son válidos.', 422);
    return query;
}

// Solo conserva respuestas autorizadas del listado. Nunca escribe en borradores ni colas de envío.
export function createVisitasOfflineList({ user, fetchPage, isOnline = () => navigator.onLine, driver = getOfflineStorageDriver(), now = Date.now }) {
    const scope = visitasListScope(user);
    const owner = Number(user?.id);
    const prefix = `list:${owner}:${hash(scope || '')}`;
    const indexId = `${prefix}:index`;
    let disposed = false, generation = 0, preparing = null, canceled = false;
    const localDatasets = new Map();
    const assertActive = () => { if (!scope) throw failure('forbidden', 'Tu cuenta no tiene permiso para consultar el listado.', 403); if (disposed) throw failure('canceled', 'Consulta cancelada.'); };
    const storage = async () => { assertActive(); await driver.init(); if (driver.effectiveEngine === 'memory' || driver.constructor?.name === 'MemoryStorageDriver') throw failure('storage', 'No se pudo guardar la lista en este dispositivo.'); return driver; };
    const serialized = async fn => {
        const previous = tasks.get(prefix) || Promise.resolve();
        const next = previous.catch(() => {}).then(() => typeof navigator !== 'undefined' && navigator.locks?.request
            ? navigator.locks.request(prefix, fn) : fn());
        tasks.set(prefix, next);
        try { return await next; } finally { if (tasks.get(prefix) === next) tasks.delete(prefix); }
    };
    const index = async () => {
        const value = await (await storage()).get(STORE, indexId);
        return value?.scope === scope ? value : { id: indexId, userId: owner, scope, pages: {} };
    };
    const keyFor = (query, page) => `${prefix}:${stable(query)}:${page}`;
    const read = async params => {
        const query = queryOf(params), page = Number(params.page || 1), manifest = await index();
        const valid = Object.entries(manifest.pages).filter(([,v]) => v.query === stable(query) && now()-v.savedAt < MAX_AGE);
        const pages = valid.map(([,v]) => v.page).sort((a,b) => a-b);
        const latest = valid.sort((a,b) => b[1].savedAt-a[1].savedAt)[0]?.[1];
        const id = keyFor(query, page);
        const value = pages.includes(page) ? await driver.get(STORE, id) : null;
        const response = value?.scope === scope && value.query === stable(query) && value.page === page ? value.response : null;
        return { response, savedAt: response ? value.savedAt : null, pages, meta: latest?.meta || null };
    };
    const save = async (params, response, expectedGeneration) => serialized(async () => {
        assertActive(); if (generation !== expectedGeneration) return;
        const query = queryOf(params), page = Number(params.page || 1);
        const pagination = response?.meta?.pagination;
        if (!Array.isArray(response?.data) || Number(pagination?.current_page) !== page || Number(pagination?.per_page) !== query.per_page
            || !Number.isSafeInteger(Number(pagination?.last_page)) || Number(pagination.last_page) < 1
            || !Number.isSafeInteger(Number(pagination?.total)) || Number(pagination.total) < 0) throw failure('invalid_response', 'El servidor devolvió un listado incompleto.');
        const bytes = stable(response).length * 2;
        if (bytes > MAX_BYTES) throw failure('storage_limit', 'Esta página supera el espacio reservado para listas descargadas.');
        const manifest = await index(), id = keyFor(query,page), savedAt = now();
        const removed = [];
        for (const [key,entry] of Object.entries(manifest.pages)) {
            if (now()-entry.savedAt >= MAX_AGE || (entry.query === stable(query) && ['total','last_page','per_page'].some(key => Number(entry.meta.pagination[key]) !== Number(pagination[key])))) {
                delete manifest.pages[key]; removed.push(key);
            }
        }
        manifest.pages[id] = { query: stable(query), page, savedAt, bytes, meta: response.meta };
        const ordered = Object.entries(manifest.pages).filter(([key]) => key !== id).sort((a,b) => a[1].savedAt-b[1].savedAt);
        ordered.push([id, manifest.pages[id]]);
        let totalBytes = ordered.reduce((sum,[,entry]) => sum+entry.bytes,0);
        while (ordered.length > MAX_PAGES || totalBytes > MAX_BYTES) {
            const [key,entry] = ordered.shift(); totalBytes -= entry.bytes; delete manifest.pages[key]; removed.push(key);
        }
        const row = { id, userId: owner, scope, query: stable(query), page, savedAt, response };
        assertActive(); if (generation !== expectedGeneration) return;
        await driver.atomicPutBatch([{ storeName: STORE, value: row }, { storeName: STORE, value: manifest }]);
        localDatasets.clear();
        for (const key of removed) if (key !== id) await driver.delete(STORE,key);
    });
    const revoke = async () => {
        generation++;
        localDatasets.clear();
        await serialized(async () => { for (const row of await (await storage()).getAll(STORE, null, { userId: owner })) if (row.userId === owner) await driver.delete(STORE,row.id); });
    };
    const cachedResult = async (params, reason) => {
        const query = queryOf(params);
        let dataset = localDatasets.get(query.tab);
        if (!dataset || now()-dataset.checkedAt > 60000 || now() >= dataset.expiresAt) {
            const manifest = await index();
            const entries = Object.entries(manifest.pages).filter(([,entry]) => JSON.parse(entry.query).tab === query.tab && now()-entry.savedAt < MAX_AGE);
            const allowed = new Set(entries.map(([id]) => id));
            // Una lectura nativa, limitada por el manifiesto; sin una consulta por cada visita.
            const stored = await driver.getAll(STORE,null,{ userId: owner });
            const rows = new Map(), known = new Map();
            const allAllowed = new Set(Object.entries(manifest.pages).filter(([,entry])=>now()-entry.savedAt<MAX_AGE).map(([id])=>id));
            for(const item of stored.filter(row=>allAllowed.has(row.id)&&row.scope===scope).sort((a,b)=>a.savedAt-b.savedAt))
                for(const row of item.response?.data||[])known.set(Number(row.id),row);
            for (const item of stored.filter(row => allowed.has(row.id) && row.scope === scope).sort((a,b) => a.savedAt-b.savedAt)) {
                for (const row of item.response?.data || []) if (Number.isSafeInteger(Number(row.id))) rows.set(Number(row.id),row);
            }
            const latest = entries.sort((a,b) => b[1].savedAt-a[1].savedAt)[0]?.[1];
            dataset = { rows: [...rows.values()], known:[...known.values()], savedAt: latest?.savedAt || null, meta: latest?.meta, checkedAt: now(),
                expiresAt: Math.min(...entries.map(([,entry]) => entry.savedAt+MAX_AGE)), available: entries.length > 0 };
            localDatasets.set(query.tab,dataset);
        }
        assertActive();
        const projected = new Map(dataset.rows.map(row=>[Number(row.id),row]));
        const operations=await driver.getAll(OFFLINE_STORES.OPERATIONS,null,{userId:owner});
        const startVisits=[...new Set(operations.filter(op=>op.type==='iniciar_visita'&&op.userId===owner
            &&!['applied','superseded'].includes(op.status)&&op.payload?.owner_empresa_id===Number(user.empresa_id))
            .map(op=>Number(op.visitaId)))];
        // Solo un inicio pendiente requiere proyectar estado sobre el listado.
        // No transportar formularios ni originales históricos para una consulta.
        const contexts=(await Promise.all(startVisits.map(visitaId=>driver.getAll(OFFLINE_STORES.CONTEXTS,
            row=>row.kind==='context'&&row.userId===owner&&row.empresaId===Number(user.empresa_id)&&row.visitaId===visitaId&&row.scope===scope,
            {userId:owner,contexts:{visitaId,empresaId:Number(user.empresa_id),kinds:['context']}})))).flat();
        for(const row of dataset.known) {
            const start=operations.find(op=>op.type==='iniciar_visita'&&op.userId===owner&&op.visitaId===Number(row.id)
                &&!['applied','superseded'].includes(op.status)&&op.payload?.owner_empresa_id===Number(user.empresa_id)
                &&op.payload?.sucursal_id===Number(row.sucursal_id));
            const authorized=start&&contexts.some(item=>item.kind==='context'&&item.userId===owner&&item.empresaId===Number(user.empresa_id)
                &&item.visitaId===Number(row.id)&&item.scope===scope&&item.context?.offline_authorization?.expires_at>now()
                &&visitasListScope(item.context.offline_authorization.actor)===scope
                &&item.context.offline_authorization.form_revision===start.payload.form_revision);
            const visit=authorized&&row.estado==='pendiente'?{...row,estado:'iniciada',inicio_pendiente_confirmacion:true}:row;
            const localStart=visit.inicio_pendiente_confirmacion||visit.inicio_recibo?.start_effect==='started';
            const startInTab=localStart&&visit.estado==='iniciada'&&(query.tab==='iniciadas_no_finalizadas'
                ||query.tab==='iniciadas'&&isTodayDate(visit.scheduled_at));
            if(projected.has(Number(row.id))||startInTab)projected.set(Number(row.id),visit);
        }
        const fields = { fecha: row => row.scheduled_at, numero: row => Number(row.id), estado: row => row.estado,
            cliente: row => row.cliente_nombre, sucursal: row => row.sucursal_nombre,
            servicio: row => row.tipo_servicio_nombre || row.form_template_nombres?.join(' ') };
        const collator = new Intl.Collator('es',{ numeric: true, sensitivity: 'base' });
        const direction = query.sort_dir === 'asc' ? 1 : -1;
        const rows = [...projected.values()].filter(row=>(query.tab!=='pendientes'||!row.estado||row.estado==='pendiente')
            && (!['pendientes','iniciadas','iniciadas_no_finalizadas'].includes(query.tab)
            || !['finalizada','aprobada','revisada','cancelada','anulada'].includes(row.estado))).sort((a,b) => {
            const left = fields[query.sort_by](a), right = fields[query.sort_by](b);
            return direction*((typeof left === 'number' && typeof right === 'number' ? left-right : collator.compare(String(left || ''),String(right || ''))) || Number(a.id)-Number(b.id));
        });
        const lastPage = Math.max(1,Math.ceil(rows.length/query.per_page));
        const page = Math.min(Number(params.page || 1),lastPage);
        return { source: 'saved', reason, missing: !dataset.available, savedAt: dataset.savedAt,
            pages: dataset.available ? Array.from({ length: lastPage },(_,i) => i+1) : [],
            coverage: { downloaded: rows.length, remoteTotal: Number(dataset.meta?.pagination?.total || 0) },
            response: { data: rows.slice((page-1)*query.per_page,page*query.per_page), meta: { ...dataset.meta,
                pagination: { current_page: page, per_page: query.per_page, total: rows.length, last_page: lastPage } } } };
    };
    const load = async (params, options = {}) => {
        assertActive(); queryOf(params);
        if (options.forceRefresh) localDatasets.clear();
        if (!isOnline()) return cachedResult(params, 'offline');
        const expectedGeneration = generation;
        let response;
        try { response = await fetchPage(params, options); }
        catch (error) {
            const status = Number(error?.response?.status ?? error?.status);
            if ([401,403].includes(status)) { await revoke().catch(() => {}); throw error; }
            if (status >= 400 && status < 500 && status !== 408 && status !== 429) throw error;
            return cachedResult(params, 'connection');
        }
        assertActive(); if (generation !== expectedGeneration) throw failure('canceled', 'Consulta cancelada.');
        let storageError = null;
        try { await save(params, response, expectedGeneration); } catch (error) { storageError = error.message; }
        if(!storageError&&(await driver.getAll(OFFLINE_STORES.OPERATIONS,null,{userId:owner}))
            .some(op=>op.userId===owner&&op.type==='iniciar_visita'&&!['applied','superseded'].includes(op.status)))return cachedResult(params,'local_start');
        if (!disposed && generation === expectedGeneration && typeof window !== 'undefined') {
            const sucursalIds = [...new Set((Array.isArray(response?.data) ? response.data : []).map(row => Number(row.sucursal_id)).filter(id => Number.isSafeInteger(id) && id > 0))];
            if (sucursalIds.length) window.dispatchEvent(new CustomEvent(VISITAS_BRANCHES_OBSERVED, { detail: { scope, sucursalIds } }));
        }
        let cached = { pages: [] };
        try { cached = await read(params); } catch { /* La lectura online sigue disponible si falla la persistencia. */ }
        return { response, source: 'server', pages: cached.pages, savedAt: cached.savedAt, storageError, missing: false };
    };
    const retainPreparedQuery = params => serialized(async()=>{
        assertActive();const manifest=await index(),query=stable(queryOf(params)),tab=queryOf(params).tab,removed=[];
        for(const [key,entry] of Object.entries(manifest.pages)) {
            if(JSON.parse(entry.query).tab===tab && entry.query!==query){delete manifest.pages[key];removed.push(key);}
        }
        if(removed.length){await driver.put(STORE,manifest);for(const key of removed)await driver.delete(STORE,key);localDatasets.clear();}
    });
    const prepare = (params, onProgress = () => {}) => {
        if (preparing) return preparing;
        canceled = false;
        preparing = (async () => {
            let lastPage = 1, downloaded = 0, expected = null;
            for (let page = 1; page <= Math.min(lastPage, MAX_PAGES); page++) {
                assertActive(); if (canceled || !isOnline()) return { complete: false, downloaded, lastPage, reason: canceled ? 'canceled' : 'offline' };
                const result = await load({ ...params, page }, { forceRefresh: true });
                if (result.source !== 'server' || result.storageError) return { complete: false, downloaded, lastPage, reason: result.storageError || 'offline' };
                const signature = stable(['total','last_page','per_page'].map(key => result.response.meta.pagination[key]));
                if (expected !== null && signature !== expected) return { complete: false, downloaded: (await read(params)).pages.length, lastPage, reason: 'changed' };
                expected = signature;
                lastPage = Number(result.response.meta.pagination.last_page); downloaded++;
                onProgress({ downloaded, lastPage });
            }
            const saved = await read(params);
            let complete = downloaded === lastPage && Array.from({ length: lastPage }, (_,i) => i+1).every(page => saved.pages.includes(page));
            if (complete) {
                const records = await driver.getAll(STORE,null,{ userId: owner });
                const ids = new Set(records.filter(row => row.scope === scope && row.query === stable(queryOf(params)) && saved.pages.includes(row.page))
                    .flatMap(row => row.response.data.map(visita => Number(visita.id))));
                if (ids.size !== Number(saved.meta.pagination.total)) return { complete: false, downloaded: saved.pages.length, lastPage, reason: 'changed' };
                await retainPreparedQuery(params);
            }
            return { complete, downloaded: saved.pages.length, lastPage, reason: downloaded < lastPage ? 'limit' : complete ? null : 'capacity' };
        })().finally(() => { preparing = null; });
        return preparing;
    };
    const prefetchNext = async (params, result) => {
        const page = Number(params.page || 1)+1;
        if (disposed || !isOnline() || result.source !== 'server' || result.storageError || page > Number(result.response?.meta?.pagination?.last_page)) return;
        const cached = await read({ ...params, page });
        if (cached.response && now()-cached.savedAt < 300000) return;
        await load({ ...params, page });
    };
    const dailyId = `${prefix}:daily`;
    const dailyState = async () => { const row = await (await storage()).get(STORE,dailyId); return row?.scope === scope ? row : null; };
    const preparedVisitIds = async (tabs) => {
        const manifest = await index(), rows = await driver.getAll(STORE, null, { userId: owner });
        const stored = new Map(rows.filter(row => row.scope === scope).map(row => [row.id, row]));
        const ids = new Set();
        for (const tab of tabs) {
            const params = { tab, per_page: 50, sort_by: 'fecha', sort_dir: 'desc' }, query = stable(queryOf(params));
            const first = await read(params), pagination = first.meta?.pagination;
            const lastPage = Number(pagination?.last_page), total = Number(pagination?.total), tabIds = new Set();
            if (!Number.isSafeInteger(lastPage) || lastPage < 1 || lastPage > MAX_PAGES || !Number.isSafeInteger(total) || total < 0) return null;
            for (let page = 1; page <= lastPage; page++) {
                const key = keyFor(queryOf(params), page), metadata = manifest.pages[key], row = stored.get(key);
                if (metadata?.query !== query || now() - metadata.savedAt >= MAX_AGE || row?.query !== query || row.page !== page
                    || !Array.isArray(row.response?.data) || Number(row.response?.meta?.pagination?.total) !== total
                    || Number(row.response?.meta?.pagination?.last_page) !== lastPage || Number(row.response?.meta?.pagination?.current_page) !== page
                    || Number(row.response?.meta?.pagination?.per_page) !== 50) return null;
                for (const visit of row.response.data) {
                    const id = Number(visit?.id);
                    if (!Number.isSafeInteger(id) || id < 1 || tabIds.has(id)) return null;
                    tabIds.add(id);
                    if (!['finalizada','aprobada','revisada','cancelada','anulada'].includes(visit.estado)) ids.add(id);
                }
            }
            if (tabIds.size !== total) return null;
        }
        assertActive(); return [...ids].sort((a, b) => a - b);
    };
    const markPrepared = async (day, timezone) => {
        assertActive(); await (await storage()).put(STORE,{ id: dailyId, userId: owner, scope, day, timezone, savedAt: now() });
    };
    const importPages = async (pages,complete=false) => {
        const expected = generation;
        for (const page of pages) { assertActive(); await save(page.params,page.response,expected); }
        if(complete) {
            const queries=new Map(pages.map(page=>[stable(queryOf(page.params)),page.params]));
            for(const params of queries.values()) {
                const cached=await read({...params,page:1});
                if(!cached.response || cached.pages.length!==Number(cached.meta?.pagination?.last_page))return false;
            }
            for(const params of queries.values())await retainPreparedQuery(params);
        }
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(VISITAS_CATALOG_CHANGED,{ detail:{ scope } }));
        return true;
    };
    const invalidate = async () => {
        await revoke();
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(VISITAS_CATALOG_CHANGED,{ detail:{ scope } }));
    };
    return { load, read, prepare, prefetchNext, dailyState, markPrepared, preparedVisitIds, importPages, invalidate,
        cancel: () => { canceled = true; }, dispose: () => { disposed = true; canceled = true; generation++; localDatasets.clear(); } };
}
